import React from "react";

/* ─────────────────────────────────────────────────────────
   DocumentViewerModal
   A self-contained, dependency-free modal for previewing an
   uploaded document (image / pdf / office file) in place.

   - Images (png, jpg, jpeg, gif, webp, bmp, svg) → <img>
   - PDF                                          → native <iframe>
   - Office (doc, docx, xls, xlsx, csv, ppt…)     → Office Online embed
   - Anything else                                → download prompt

   Uses only inline styles so it renders identically inside the
   authenticated app and the public approval page.
───────────────────────────────────────────────────────── */

export interface DocumentViewerModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Fully-qualified URL of the file to preview. */
    fileUrl: string;
    /** File extension/type e.g. "pdf", "png", "docx". Case-insensitive. */
    fileType?: string;
    /** Display name shown in the header. */
    fileName?: string;
}

type ViewerKind = "image" | "pdf" | "office" | "unknown";

const IMAGE_TYPES = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];
const OFFICE_TYPES = ["doc", "docx", "xls", "xlsx", "csv", "ppt", "pptx"];

const resolveKind = (fileType?: string, fileUrl?: string): ViewerKind => {
    let ext = (fileType || "").toLowerCase().replace(/^\./, "");
    // Fall back to deriving the extension from the URL if type is missing.
    if (!ext && fileUrl) {
        const clean = fileUrl.split("?")[0].split("#")[0];
        ext = (clean.split(".").pop() || "").toLowerCase();
    }
    if (ext === "pdf") return "pdf";
    if (IMAGE_TYPES.includes(ext)) return "image";
    if (OFFICE_TYPES.includes(ext)) return "office";
    return "unknown";
};

const CloseIcon: React.FC = () => (
    <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
        <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const DownloadIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
        <path d="M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5M4 15.5h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
    isOpen,
    onClose,
    fileUrl,
    fileType,
    fileName = "Document",
}) => {
    // Lock body scroll + close on Escape while open.
    React.useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const kind = resolveKind(fileType, fileUrl);

    const renderBody = () => {
        switch (kind) {
            case "image":
                return (
                    <div style={styles.imageWrap}>
                        <img src={fileUrl} alt={fileName} style={styles.image} />
                    </div>
                );
            case "pdf":
                return (
                    <iframe
                        title={fileName}
                        src={fileUrl}
                        style={styles.frame}
                    />
                );
            case "office":
                return (
                    <iframe
                        title={fileName}
                        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
                        style={styles.frame}
                    />
                );
            default:
                return (
                    <div style={styles.fallback}>
                        <p style={{ fontSize: "15px", fontWeight: 600, color: "#111827", margin: "0 0 6px" }}>
                            Preview not available
                        </p>
                        <p style={{ fontSize: "13px", color: "#6B7280", margin: "0 0 18px" }}>
                            This file type can’t be previewed in the browser. You can download it instead.
                        </p>
                        <a href={fileUrl} download target="_blank" rel="noreferrer" style={styles.fallbackBtn}>
                            <DownloadIcon /> <span style={{ marginLeft: 6 }}>Download</span>
                        </a>
                    </div>
                );
        }
    };

    return (
        <div
            style={styles.overlay}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div style={styles.modal}>
                <div style={styles.header}>
                    <span style={styles.title} title={fileName}>{fileName}</span>
                    <div style={styles.headerActions}>
                        <a
                            href={fileUrl}
                            download
                            target="_blank"
                            rel="noreferrer"
                            style={styles.iconBtn}
                            title="Download"
                        >
                            <DownloadIcon />
                        </a>
                        <button type="button" onClick={onClose} style={styles.iconBtn} title="Close">
                            <CloseIcon />
                        </button>
                    </div>
                </div>
                <div style={styles.body}>{renderBody()}</div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(17, 24, 39, 0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100000,
        padding: "24px",
    },
    modal: {
        backgroundColor: "#FFFFFF",
        borderRadius: "10px",
        width: "min(1000px, 100%)",
        height: "min(88vh, 100%)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        borderBottom: "1px solid #E5E7EB",
        flexShrink: 0,
    },
    title: {
        fontSize: "15px",
        fontWeight: 600,
        color: "#111827",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        marginRight: "12px",
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        flexShrink: 0,
    },
    iconBtn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "34px",
        height: "34px",
        borderRadius: "6px",
        border: "none",
        background: "transparent",
        color: "#6B7280",
        cursor: "pointer",
        textDecoration: "none",
    },
    body: {
        flex: 1,
        minHeight: 0,
        backgroundColor: "#F3F4F6",
        display: "flex",
    },
    frame: {
        border: "none",
        width: "100%",
        height: "100%",
    },
    imageWrap: {
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "auto",
        padding: "16px",
    },
    image: {
        maxWidth: "100%",
        maxHeight: "100%",
        objectFit: "contain",
    },
    fallback: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "24px",
    },
    fallbackBtn: {
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: "#3F83F8",
        color: "#FFFFFF",
        padding: "8px 18px",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: 500,
        textDecoration: "none",
    },
};

export default DocumentViewerModal;
